import { create } from "zustand";

import { api, hasToken, setToken } from "../api/client";
import type { LoginResponse, Message, UserInfo } from "../api/types";

interface AuthState {
  authorized: boolean;
  user: UserInfo | null;
  connecting: boolean;
  reset: () => void;
  loadUser: () => Promise<UserInfo | null>;
  ensureUser: () => Promise<void>;
  signIn: (
    login: string,
    password: string,
    signal?: AbortSignal,
  ) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  authorized: hasToken(),
  user: null,
  connecting: false,

  reset() {
    setToken(null);
    set({ authorized: false, user: null });
  },

  async loadUser() {
    if (!get().authorized) return null;
    const user = await api.get<UserInfo>("/auth/me");
    set({ user });
    return user;
  },

  async ensureUser() {
    const state = get();
    if (!state.authorized || state.user) return;
    try {
      await state.loadUser();
    } catch {
      set({ user: null });
    }
  },

  async signIn(login, password, signal) {
    set({ connecting: true });
    try {
      const response = await api.post<LoginResponse>(
        "/auth/login",
        { login, password },
        { signal },
      );
      setToken(response.token);
      set({ authorized: true, user: response.user });
    } finally {
      set({ connecting: false });
    }
  },

  async signOut() {
    await api.post<Message>("/auth/logout").catch(() => null);
    get().reset();
  },
}));
